import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react'
import axios from 'axios'
import { toast } from 'sonner'
import {
  AuthState,
  AuthContextValue,
  ClerkPublicMetadata,
  OnboardingData,
  UserStatus,
  ProviderSyncStatus,
  VerifyUserResponse,
} from '@/types/auth'
import { useAppDispatch } from '@/store/hooks'
import { fetchCurrentUser } from '@/store/slices/userSlice'
import { loadUserMenus, logout as logoutAction } from '@/store/slices/authSlice'
import { checkIfTokenExpired } from '@/api'
import { isWebView, postToNative } from '@/utils/webview'
import { buildLoginRedirectUrl } from '@/utils/authRedirect'
import { announceSignOut, isDeliberateSignOut } from '@/utils/authEvents'
import i18n from '@/i18n'

const SHOW_LEGACY_LOGIN = import.meta.env.VITE_SHOW_LEGACY_LOGIN === 'true'
const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * 使用认证上下文
 */
export function useAuthContext() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider')
  }
  return context
}

/**
 * 创建专用的 axios 实例用于认证相关请求
 * 避免与全局拦截器冲突
 */
const authAxios = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '300000', 10),
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * 认证提供者组件
 *
 * 核心职责：
 * 1. 监听 Clerk 登录状态
 * 2. 根据 publicMetadata 判断用户状态
 * 3. 自动同步用户到数据库
 * 4. 管理认证状态转换
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (!CLERK_ENABLED) {
    return <LegacyOnlyAuthProvider>{children}</LegacyOnlyAuthProvider>
  }
  return <ClerkAuthProvider>{children}</ClerkAuthProvider>
}

/**
 * Legacy-only 认证提供者
 * 当没有 Clerk publishable key 时使用，只支持传统 JWT 登录
 */
function LegacyOnlyAuthProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const [authState, setAuthState] = useState<AuthState>(AuthState.INITIALIZING)
  const userInfoLoadedRef = useRef(false)

  useEffect(() => {
    // 检查 legacy token
    const token = localStorage.getItem('ACCESS_TOKEN')
    if (token) {
      try {
        if (!checkIfTokenExpired(token)) {
          console.log('✅ [Auth] Legacy-only mode: valid JWT token found')
          setAuthState(AuthState.AUTHENTICATED)
          if (!userInfoLoadedRef.current) {
            void loadUserInfo()
          }
          return
        }
      } catch {
        // token invalid
      }
    }
    setAuthState(AuthState.UNAUTHENTICATED)
  }, [])

  const loadUserInfo = async () => {
    if (userInfoLoadedRef.current) return
    try {
      await dispatch(fetchCurrentUser()).unwrap()
      await dispatch(loadUserMenus()).unwrap()
      userInfoLoadedRef.current = true
    } catch (err) {
      console.error('Failed to load user info or menus:', err)
      toast.error(i18n.t('auth:toast.failedToLoadUserData'), {
        description: 'Some features may not work correctly',
      })
    }
  }

  // 监听 401 错误事件
  useEffect(() => {
    const handleUnauthorized = (event: Event) => {
      // 主动退出发的是同一个事件(见 utils/authEvents):那条路自己会清状态,这里再登出
      // 一次只会把用户硬跳到登录页。
      if (isDeliberateSignOut(event)) return
      console.log('🚨 [Auth] Legacy-only mode: 401 received, logging out')
      localStorage.removeItem('ACCESS_TOKEN')
      localStorage.removeItem('REFRESH_TOKEN')
      setAuthState(AuthState.UNAUTHENTICATED)
      userInfoLoadedRef.current = false
      void dispatch(logoutAction())
      window.location.href = buildLoginRedirectUrl()
    }
    window.addEventListener('auth:unauthorized', handleUnauthorized as EventListener)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized as EventListener)
  }, [dispatch])

  const signOut = async () => {
    announceSignOut()
    try {
      await dispatch(logoutAction()).unwrap()
    } catch (err) {
      console.error('Redux logout failed:', err)
    }
    localStorage.removeItem('ACCESS_TOKEN')
    localStorage.removeItem('REFRESH_TOKEN')
    setAuthState(AuthState.UNAUTHENTICATED)
    userInfoLoadedRef.current = false
  }

  const contextValue: AuthContextValue = {
    state: authState,
    user: null,
    error: undefined,
    isRecoverableError: true,
    completeOnboarding: async () => { throw new Error('Onboarding not available in legacy mode') },
    signOut,
    refreshAuth: async () => {
      const token = localStorage.getItem('ACCESS_TOKEN')
      if (token && !checkIfTokenExpired(token)) {
        setAuthState(AuthState.AUTHENTICATED)
        if (!userInfoLoadedRef.current) {
          await loadUserInfo()
        }
      } else {
        setAuthState(AuthState.UNAUTHENTICATED)
      }
    },
    clerkLoadFailed: true,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded: clerkLoaded, isSignedIn } = useUser()
  const { getToken } = useClerkAuth()
  const clerk = useClerk()
  const dispatch = useAppDispatch()

  const [authState, setAuthState] = useState<AuthState>(AuthState.INITIALIZING)
  const [error, setError] = useState<string>()
  const [isRecoverableError, setIsRecoverableError] = useState<boolean>(true)
  const [clerkLoadFailed, setClerkLoadFailed] = useState(false)

  // 🔥 防止并发调用的锁
  const verifyLockRef = useRef(false) // 防止重复验证
  const onboardingLockRef = useRef(false)
  const userInfoLoadedRef = useRef(false) // 防止重复加载用户信息
  const signOutLockRef = useRef(false) // 防止重复登出

  // 暴露 getToken 到全局，供 axios 拦截器使用
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__getClerkToken = isSignedIn ? getToken : undefined
    }
    return () => { window.__getClerkToken = undefined }
  }, [getToken, isSignedIn])

  // Clerk 加载超时检测：若 Clerk 长时间未加载则降级到 legacy 流程
  useEffect(() => {
    if (clerkLoaded) return
    const timer = setTimeout(() => {
      if (!clerkLoaded) {
        console.warn('⚠️ [Auth] Clerk failed to load within timeout, falling back to legacy auth')
        // 清除 window.__getClerkToken，防止 axios 拦截器在 getToken() 上永久挂起
        if (typeof window !== 'undefined') {
          window.__getClerkToken = undefined
        }
        setClerkLoadFailed(true)
      }
    }, 8000)
    return () => clearTimeout(timer)
  }, [clerkLoaded])

  // 监听认证状态变化
  // 注意：只监听 user?.id 而不是整个 user 对象，避免 metadata 更新时触发不必要的重新执行
  useEffect(() => {
    void handleAuthStateTransition()
  }, [clerkLoaded, clerkLoadFailed, isSignedIn, user?.id])

  /**
   * 核心：认证状态转换处理
   *
   * 简化逻辑：不再检查 metadata，直接调用后端验证
   * 原因：
   * 1. Platform 用户没有 tenant_id（正常情况），检查 metadata 会误判
   * 2. 后端通过 userStatus 明确告知用户状态，更可靠
   * 3. 代码更简单，逻辑更清晰
   */
  const handleAuthStateTransition = async () => {
    try {
      // ===== 0. WebView 模式 - 跳过 Clerk，直接用 localStorage token =====
      if (isWebView()) {
        const token = localStorage.getItem('ACCESS_TOKEN')
        if (token) {
          // WebView 模式下只检查 token 是否存在，不检查过期时间
          // 因为 App 端负责 token 生命周期管理（每 50 秒刷新）
          console.log('✅ [Auth] WebView mode: using injected token')
          setAuthState(AuthState.AUTHENTICATED)
          if (!userInfoLoadedRef.current) {
            await loadUserInfoAndMenus()
          }
          return
        }
        // Token 还没注入（时序问题），保持 INITIALIZING 等待 tokenInjected 事件
        console.log('⏳ [Auth] WebView mode: waiting for token injection')
        setAuthState(AuthState.INITIALIZING)
        return
      }

      // ===== 1. Clerk SDK 初始化 =====
      if (!clerkLoaded && !clerkLoadFailed) {
        setAuthState(AuthState.INITIALIZING)
        return
      }

      // ===== 2. 未登录 =====
      if (!isSignedIn || !user) {
        // Clerk 不可用或开启了 legacy 模式时，检查传统 JWT Token
        if ((clerkLoadFailed || SHOW_LEGACY_LOGIN) && hasValidLegacyToken()) {
          console.log('✅ [Auth] Legacy JWT token detected, authenticating via legacy flow')
          setAuthState(AuthState.AUTHENTICATED)
          if (!userInfoLoadedRef.current) {
            await loadUserInfoAndMenus()
          }
          return
        }

        setAuthState(AuthState.UNAUTHENTICATED)
        clearError()
        // 🔥 重置加载标记（重要：登出时必须重置）
        userInfoLoadedRef.current = false
        return
      }

      // ===== 3. 已登录 - 验证用户状态 =====
      // ⚡ Fast path（与 mobile 冷启动优化对齐）：Clerk publicMetadata 已有 dbUserId，
      // 说明后端早已同步过该用户，跳过阻塞式 verify-user（它内部要验签+多次查库+可能
      // 回写 Clerk metadata，放在每次整页加载的关键路径上非常重）。
      // - 首次登录 metadata 无 dbUserId → 仍走完整 verify-user（含 onboarding 判定）
      // - inactive 用户由后端 token 层强制 401 兜底（全端点生效），fetch 用户信息时
      //   会触发 auth:unauthorized → 统一登出，不会滞留在页面里
      const cachedDbUserId = (user.publicMetadata as ClerkPublicMetadata | undefined)?.dbUserId
      if (cachedDbUserId) {
        console.log('✅ [Auth] verify-user skipped (dbUserId already in publicMetadata):', cachedDbUserId)
        setAuthState(AuthState.AUTHENTICATED)
        if (!userInfoLoadedRef.current) {
          await loadUserInfoAndMenus()
        }
        return
      }

      setAuthState(AuthState.NEED_USER_SYNC) // 设置为验证中状态

      // 🔥 直接调用后端验证，不检查 metadata
      if (!verifyLockRef.current) {
        await verifyUserActualState()
      }
    } catch (err: any) {
      console.error('❌ [Auth] State transition error:', err)
      setError(err.message || 'Authentication failed')
      setIsRecoverableError(true) // 一般性错误是可恢复的
      setAuthState(AuthState.AUTH_ERROR)
    }
  }

  /**
   * 验证用户状态
   *
   * 调用后端 /verify-user 接口，获取用户的最新状态
   * 后端会根据 Token 中的信息：
   * 1. 验证或创建用户
   * 2. 检查用户是否需要 onboarding
   * 3. 返回 userStatus（active/pending_organization/inactive）
   * 4. 如果需要，同步更新 Clerk metadata
   */
  const verifyUserActualState = async () => {
    if (!user || verifyLockRef.current) return

    // 🔥 加锁，防止并发调用
    verifyLockRef.current = true

    try {
      const clerkToken = await getToken()

      const response = await authAxios.post(
        '/system/user/verify-user',
        {
          provider: 'clerk',
          externalUserId: user.id,
          email: user.primaryEmailAddress?.emailAddress,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          profileImageUrl: user.imageUrl,
        },
        {
          headers: {
            Authorization: `Bearer ${clerkToken}`,
          },
        }
      )

      const { userStatus, providerSyncStatus } = response.data as VerifyUserResponse

      // 如果后端更新了 Clerk metadata，刷新一次 session
      if (providerSyncStatus === ProviderSyncStatus.SYNCED) {
        await clerk.session?.reload()
        console.log('✅ [Auth] Clerk session reloaded')
      }

      // 根据 userStatus 设置前端状态
      if (userStatus === UserStatus.ACTIVE) {
        console.log('✅ [Auth] User is ACTIVE')
        setAuthState(AuthState.AUTHENTICATED)

        // 加载用户信息和菜单权限
        if (!userInfoLoadedRef.current) {
          await loadUserInfoAndMenus()
        }
      } else if (userStatus === UserStatus.PENDING_ORGANIZATION) {
        console.log('🟡 [Auth] User needs onboarding')
        setAuthState(AuthState.NEEDS_ONBOARDING)
      } else if (userStatus === UserStatus.INACTIVE) {
        console.log('❌ [Auth] User account is disabled')
        setError(
          'Your account has been disabled. Please contact your administrator for assistance.'
        )
        setIsRecoverableError(false)
        setAuthState(AuthState.AUTH_ERROR)
      }
    } catch (err: any) {
      console.error('❌ [Auth] Failed to verify user:', err)

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to verify user'

      setError(errorMessage)
      setIsRecoverableError(true)
      setAuthState(AuthState.AUTH_ERROR)

      toast.error(i18n.t('auth:toast.authenticationFailed'), {
        description: errorMessage,
      })
    } finally {
      verifyLockRef.current = false
    }
  }

  /**
   * 完成 onboarding
   *
   * 从 onboarding 页面调用
   * 只传递组织信息，用户信息从 Token 获取
   */
  const completeOnboarding = async (data: OnboardingData) => {
    if (!user || onboardingLockRef.current) return

    // 🔥 加锁，防止重复提交
    onboardingLockRef.current = true

    try {
      const clerkToken = await getToken()

      const response = await authAxios.post(
        '/system/user/verify-user',
        {
          provider: 'clerk', // 🔥 必填
          externalUserId: user.id, // 🔥 Clerk User ID
          email: user.primaryEmailAddress?.emailAddress, // 传递用户信息
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          profileImageUrl: user.imageUrl,
          // 🔥 关键：传入组织信息（companyName 或 accessCode）
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${clerkToken}`,
          },
        }
      )

      // 🔥 后端返回格式：{ userStatus, providerSyncStatus }
      const { userStatus, providerSyncStatus } = response.data as VerifyUserResponse

      // 🔥 处理 Provider 同步状态
      if (providerSyncStatus === ProviderSyncStatus.SYNCED) {
        // 🔥 优化：后端已经更新完 Clerk metadata，只需刷新一次缓存
        await clerk.session?.reload()
        console.log('✅ [Auth] Clerk session reloaded after onboarding')
      }

      // 验证用户状态
      if (userStatus === UserStatus.ACTIVE) {
        toast.success(i18n.t('auth:toast.welcomeToOrg'))
        console.log('✅ [Auth] Onboarding completed, user is ACTIVE')
        setAuthState(AuthState.AUTHENTICATED)

        // 加载用户信息和菜单权限
        if (!userInfoLoadedRef.current) {
          await loadUserInfoAndMenus()
        }
      } else {
        // 理论上不应该到这里，因为 onboarding 完成后应该是 active
        throw new Error('Onboarding completed but user is not active')
      }
    } catch (err: any) {
      console.error('❌ [Auth] Failed to complete onboarding:', err)

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        err.message ||
        'Failed to complete onboarding'

      setError(errorMessage)
      setIsRecoverableError(true) // Onboarding 错误是可恢复的

      toast.error(i18n.t('auth:toast.onboardingFailed'), {
        description: errorMessage,
      })

      throw err
    } finally {
      // 🔥 解锁
      onboardingLockRef.current = false
    }
  }

  /**
   * 🔥 加载用户信息和菜单权限
   *
   * 在用户认证成功后调用，与旧版登录流程保持一致
   */
  const loadUserInfoAndMenus = async () => {
    if (userInfoLoadedRef.current) {
      return
    }

    try {
      // 🔥 并行加载用户信息（/system/user/current/info）与菜单权限（两者互相独立，
      // 串行会把首屏关键路径拉长一个 RTT）
      await Promise.all([
        dispatch(fetchCurrentUser()).unwrap(),
        dispatch(loadUserMenus()).unwrap(),
      ])

      // 标记已加载
      userInfoLoadedRef.current = true
    } catch (err: any) {
      console.error('Failed to load user info or menus:', err)
      // 不阻塞认证流程，只记录错误
      toast.error(i18n.t('auth:toast.failedToLoadUserData'), {
        description: 'Some features may not work correctly',
      })
    }
  }

  /**
   * 检查是否有有效的传统 JWT Token（仅 localdev 模式使用）
   */
  const hasValidLegacyToken = (): boolean => {
    const token = localStorage.getItem('ACCESS_TOKEN')
    if (!token) return false
    try {
      return !checkIfTokenExpired(token)
    } catch {
      return false
    }
  }

  /**
   * 统一的退出登录逻辑
   *
   * 这是系统中唯一的退出登录入口，确保所有退出操作都经过相同的流程：
   * 1. 调用后端 API 清除服务器端 session
   * 2. 清除 Redux 状态（包括菜单权限）
   * 3. 清除 Clerk session
   * 4. 更新本地状态
   *
   * 特性：
   * - 防重入：使用锁机制防止并发退出
   * - 容错性：即使某个步骤失败，也会继续执行后续步骤
   * - 完整性：确保所有状态都被正确清除
   */
  const signOut = async () => {
    // 防重入检查
    if (signOutLockRef.current) {
      console.warn('⚠️ [统一退出] 退出操作正在进行中，忽略重复调用')
      return
    }

    // 广播「用户主动退出」——各模块自己的 auth:unauthorized 监听器据此清私有缓存 /
    // 断长连接(见 utils/authEvents)。至多成功派发一次:正常路径派发一次,派发本身出错时
    // 由下面的兜底 catch 再试一次,不会重复通知。
    let announced = false
    const announceOnce = () => {
      if (announced) return
      announceSignOut()
      announced = true
    }

    try {
      signOutLockRef.current = true
      announceOnce()
      console.log('🚪 [统一退出] 开始退出登录...')

      // 1. 调用 Redux logout action（会调用后端 API + 清除菜单权限）
      try {
        await dispatch(logoutAction()).unwrap()
        console.log('✅ [统一退出] Redux 登出成功（后端 API + 菜单权限已清除）')
      } catch (apiError) {
        console.error('❌ [统一退出] Redux 登出失败，但继续本地登出', apiError)
        // 即使后端登出失败，也继续执行本地登出
      }

      // 2. 清除 Clerk session（容错处理）
      try {
        await clerk.signOut()
        console.log('✅ [统一退出] Clerk 登出成功')
      } catch (clerkError) {
        console.error('❌ [统一退出] Clerk 登出失败，但继续清除本地状态', clerkError)
        // 即使 Clerk 登出失败，也继续清除本地状态
        // 用户下次打开应用时 Clerk 会重新初始化
      }

      // 3. 通知各模块清除私有内存缓存 / 断开长连接（跨用户隔离）
      //    统一退出不 reload 页面，模块级 Map/Set 会活到下一个用户登入之后。
      //    每个模块自己已经监听 auth:unauthorized（401 那条路），这里派发同一个事件、
      //    带 reason: 'sign_out' 复用它们；会「再登出 / 跳登录页」的监听者按 reason 跳过。
      announceOnce()

      // 4. 更新本地状态（确保无论如何都会执行）
      setAuthState(AuthState.UNAUTHENTICATED)
      clearError()
      userInfoLoadedRef.current = false

      console.log('✅ [统一退出] 退出登录完成')
    } catch (err: any) {
      console.error('❌ [统一退出] 退出登录过程中发生未预期的错误:', err)

      // 兜底路径也要广播一次：出错的那一步可能正是广播本身（或它之前的某步），
      // 而模块级缓存没清掉 = 下一个登入的用户继承上一个用户的私有数据。
      try {
        announceOnce()
      } catch (announceError) {
        console.error('❌ [统一退出] 广播退出事件失败:', announceError)
      }

      // 即使发生错误，也强制清除本地状态
      try {
        setAuthState(AuthState.UNAUTHENTICATED)
        clearError()
        userInfoLoadedRef.current = false
      } catch (stateError) {
        console.error('❌ [统一退出] 清除本地状态失败:', stateError)
      }

      toast.error(i18n.t('auth:toast.failedToSignOut'))
    } finally {
      // 释放锁
      signOutLockRef.current = false
    }
  }

  /**
   * WebView 模式：监听 tokenInjected 事件
   * App 端通过 injectedJavaScriptBeforeContentLoaded 注入 token 后会派发此事件
   */
  useEffect(() => {
    if (!isWebView()) return

    const handleTokenInjected = () => {
      console.log('✅ [Auth] Token injected from native app, re-checking auth')
      void handleAuthStateTransition()
    }

    window.addEventListener('tokenInjected', handleTokenInjected)
    return () => window.removeEventListener('tokenInjected', handleTokenInjected)
  }, [])

  /**
   * 监听 401 错误事件，触发统一的退出逻辑
   */
  useEffect(() => {
    const handleUnauthorized = (event: CustomEvent) => {
      // 用户主动退出发的是同一个事件(见 utils/authEvents),它本来就走在统一退出里:
      // 这里再调一次 signOut 会被防重入锁挡下，然后 .then 把用户硬跳到登录页 ——
      // 主动退出后跳哪里由调用方决定，不该由这个 401 处理器代劳。
      if (isDeliberateSignOut(event)) return
      console.log('🚨 AuthContext: 收到 401 错误事件，触发统一退出逻辑')
      console.log('🚨 错误详情:', event.detail)

      // WebView 模式：通知 App 刷新 token，不跳转登录页
      if (isWebView()) {
        console.log('🔄 [Auth] WebView 401: requesting token refresh from native app')
        postToNative('TOKEN_REFRESH_REQUEST', { reason: '401' })
        return
      }

      // 在 signOut 之前快照登录回跳 URL，避免 signOut 异步过程中地址变化
      const loginUrl = buildLoginRedirectUrl()

      // 调用统一的退出逻辑
      // signOut 会处理所有退出步骤：后端 API、Clerk、Redux、本地状态
      signOut().then(() => {
        // 退出完成后跳转到登录页
        window.location.href = loginUrl
      }).catch((err) => {
        console.error('❌ AuthContext: 处理 401 错误时退出失败:', err)
        // 即使退出失败，也强制跳转到登录页
        window.location.href = loginUrl
      })
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized as EventListener)

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized as EventListener)
    }
  }, []) // signOut 使用 ref，不需要作为依赖

  /**
   * 清除错误状态
   */
  const clearError = () => {
    setError(undefined)
    setIsRecoverableError(true) // 重置为默认值
  }

  const contextValue: AuthContextValue = {
    state: authState,
    user,
    error,
    isRecoverableError,
    completeOnboarding,
    signOut,
    refreshAuth: handleAuthStateTransition,
    clerkLoadFailed,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

/**
 * TypeScript 全局类型扩展
 */
declare global {
  interface Window {
    __getClerkToken?: () => Promise<string | null>
  }
}
